import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Send, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MIN_PLEDGE = 25000;

const INVESTOR_TYPES = [
  { value: 'Angel Investor', label: 'Angel Investor' },
  { value: 'Individual HNWI', label: 'Individual HNWI' },
  { value: 'Family Office', label: 'Family Office' },
  { value: 'Venture Capital / Private Equity', label: 'Venture Capital / Private Equity' },
  { value: 'Strategic / Corporate Investor', label: 'Strategic / Corporate Investor' }
];

const INVESTMENT_TIMELINES = [
  { value: 'Immediate', label: 'Immediate' },
  { value: 'Within 1–3 months', label: 'Within 1–3 months' },
  { value: 'Within 3–6 months', label: 'Within 3–6 months' }
];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-MY', {
    style: 'currency',
    currency: 'MYR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount).replace('MYR', 'RM');
};

export const PledgeForm = ({ onSuccess, remainingAllocation, isNearingCap }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    organisation: '',
    investor_type: '',
    pledge_amount: '',
    investment_timeline: '',
    message: '',
    display_name: true
  });
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    if (!formData.investor_type) {
      newErrors.investor_type = 'Please select investor type';
    }
    
    const amount = parseFloat(formData.pledge_amount);
    if (!formData.pledge_amount || isNaN(amount)) {
      newErrors.pledge_amount = 'Pledge amount is required';
    } else if (amount < MIN_PLEDGE) {
      newErrors.pledge_amount = `Minimum pledge is ${formatCurrency(MIN_PLEDGE)}`;
    } else if (amount > remainingAllocation) {
      newErrors.pledge_amount = `Exceeds remaining allocation of ${formatCurrency(remainingAllocation)}`;
    }
    
    if (!formData.investment_timeline) {
      newErrors.investment_timeline = 'Please select investment timeline';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }
    
    setLoading(true);
    
    try {
      const payload = {
        ...formData,
        pledge_amount: parseFloat(formData.pledge_amount),
        organisation: formData.organisation || null,
        message: formData.message || null
      };
      
      await axios.post(`${API}/pledges`, payload);
      
      setSubmitted(true);
      toast.success('Pledge submitted successfully!');
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error submitting pledge:', error);
      const message = error.response?.data?.detail || 'Failed to submit pledge. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      full_name: '',
      email: '',
      organisation: '',
      investor_type: '',
      pledge_amount: '',
      investment_timeline: '',
      message: '',
      display_name: true
    });
    setErrors({});
    setSubmitted(false);
  };

  if (submitted) {
    return (
      <Card className="border shadow-lg">
        <CardContent className="p-8 text-center">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-success-light mb-6">
            <CheckCircle className="h-8 w-8 text-success" />
          </div>
          <h3 className="text-2xl font-display font-bold text-foreground mb-3">
            Thank You for Your Interest
          </h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Your pledge has been recorded. Our team will be in touch with next steps and formal documentation.
          </p>
          <Button onClick={handleReset} variant="outline">
            Submit Another Pledge
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border shadow-lg">
      <CardHeader className="pb-4">
        <CardTitle className="text-xl font-display">Submit Your Investment Pledge</CardTitle>
        <CardDescription>
          Express your interest in Physiomobile&apos;s Pre-Series A round
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Warning banner if nearing cap */}
          {isNearingCap && (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-warning-light border border-warning/20">
              <AlertTriangle className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-warning">Limited Allocation Remaining</p>
                <p className="text-muted-foreground">
                  Only {formatCurrency(remainingAllocation)} remaining in this round.
                </p>
              </div>
            </div>
          )}
          
          {/* Full Name */}
          <div className="space-y-2">
            <Label htmlFor="full_name">Full Name *</Label>
            <Input
              id="full_name"
              placeholder="Enter your full name"
              value={formData.full_name}
              onChange={(e) => handleInputChange('full_name', e.target.value)}
              className={errors.full_name ? 'border-destructive' : ''}
            />
            {errors.full_name && (
              <p className="text-xs text-destructive">{errors.full_name}</p>
            )}
          </div>
          
          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={errors.email ? 'border-destructive' : ''}
            />
            {errors.email && (
              <p className="text-xs text-destructive">{errors.email}</p>
            )}
          </div>
          
          {/* Organisation */}
          <div className="space-y-2">
            <Label htmlFor="organisation">Organisation / Fund Name (Optional)</Label>
            <Input
              id="organisation"
              placeholder="Your organisation or fund"
              value={formData.organisation}
              onChange={(e) => handleInputChange('organisation', e.target.value)}
            />
          </div>
          
          {/* Investor Type */}
          <div className="space-y-2">
            <Label>Investor Type *</Label>
            <Select
              value={formData.investor_type}
              onValueChange={(value) => handleInputChange('investor_type', value)}
            >
              <SelectTrigger className={errors.investor_type ? 'border-destructive' : ''}>
                <SelectValue placeholder="Select investor type" />
              </SelectTrigger>
              <SelectContent>
                {INVESTOR_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.investor_type && (
              <p className="text-xs text-destructive">{errors.investor_type}</p>
            )}
          </div>
          
          {/* Pledge Amount */}
          <div className="space-y-2">
            <Label htmlFor="pledge_amount">
              Intended Investment Amount (RM) *
              <span className="text-xs text-muted-foreground ml-2">
                Minimum: {formatCurrency(MIN_PLEDGE)}
              </span>
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                RM
              </span>
              <Input
                id="pledge_amount"
                type="number"
                placeholder="25,000"
                min={MIN_PLEDGE}
                step={1000}
                value={formData.pledge_amount}
                onChange={(e) => handleInputChange('pledge_amount', e.target.value)}
                className={`pl-10 ${errors.pledge_amount ? 'border-destructive' : ''}`}
              />
            </div>
            {errors.pledge_amount && (
              <p className="text-xs text-destructive">{errors.pledge_amount}</p>
            )}
          </div>
          
          {/* Investment Timeline */}
          <div className="space-y-2">
            <Label>Investment Timeline *</Label>
            <Select
              value={formData.investment_timeline}
              onValueChange={(value) => handleInputChange('investment_timeline', value)}
            >
              <SelectTrigger className={errors.investment_timeline ? 'border-destructive' : ''}>
                <SelectValue placeholder="Select timeline" />
              </SelectTrigger>
              <SelectContent>
                {INVESTMENT_TIMELINES.map((timeline) => (
                  <SelectItem key={timeline.value} value={timeline.value}>
                    {timeline.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.investment_timeline && (
              <p className="text-xs text-destructive">{errors.investment_timeline}</p>
            )}
          </div>
          
          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="message">Message to Physiomobile Team (Optional)</Label>
            <Textarea
              id="message"
              placeholder="Any questions or comments..."
              rows={3}
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
            />
          </div>
          
          {/* Display Name Preference */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="display_name"
              checked={formData.display_name}
              onCheckedChange={(checked) => handleInputChange('display_name', checked)}
            />
            <Label htmlFor="display_name" className="text-sm font-normal cursor-pointer">
              Display my name on the pledge activity feed (can be anonymized)
            </Label>
          </div>
          
          {/* Submit Button */}
          <Button 
            type="submit" 
            className="w-full h-12 text-base font-medium"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Submit Pledge
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default PledgeForm;
